import { DateTime } from "luxon";
import {
  knownDentalCcamActes,
  type CategorizedLogosActeData,
  type PecDocumentListItem,
  type patientQuotesFromRpc,
} from "cadendar-shared";
import type { Consultation } from "./consultationsApi";

export type PatientOverviewQuote = ReturnType<
  typeof patientQuotesFromRpc
>[number];

const knownActes = new Map(
  knownDentalCcamActes.map((acte) => [acte.code, acte]),
);

export function getPatientDate(value: string) {
  return DateTime.fromISO(value, { zone: "Europe/Paris" });
}

export function getPatientDateKey(value: string) {
  return getPatientDate(value).toISODate();
}

export function getActeLabel(acte: CategorizedLogosActeData) {
  const known = acte.ngap_ccam_code
    ? knownActes.get(acte.ngap_ccam_code)
    : undefined;
  return (
    known?.shortLabel ||
    acte.description ||
    acte.acte_code ||
    "Acte sans libellé"
  );
}

export function getClinicalSummaryEntries(actes: CategorizedLogosActeData[]) {
  const groups = new Map<
    string,
    {
      key: string;
      year: string;
      month: string;
      shortLabel: string;
      teeth: Set<number>;
      dates: Set<string>;
      sortValue: number;
    }
  >();

  for (const acte of actes) {
    const known = acte.ngap_ccam_code
      ? knownActes.get(acte.ngap_ccam_code)
      : undefined;
    if (acte.category_type !== "with_code" || known?.impact !== "high")
      continue;
    const date = getPatientDate(acte.timestamp).setLocale("fr");
    const monthKey = date.isValid ? date.toFormat("yyyy-MM") : "unknown";
    const key = `${monthKey}__${known.shortLabel}`;
    const group = groups.get(key) ?? {
      key,
      year: date.isValid ? date.toFormat("yyyy") : "Date inconnue",
      month: date.isValid ? date.toFormat("LLLL") : "Date inconnue",
      shortLabel: known.shortLabel,
      teeth: new Set<number>(),
      dates: new Set<string>(),
      sortValue: date.isValid ? date.startOf("month").toMillis() : -Infinity,
    };
    for (const tooth of acte.teeth.match(/\d{1,3}/g) ?? []) {
      if (Number(tooth) > 0) group.teeth.add(Number(tooth));
    }
    const dateKey = date.toISODate();
    if (dateKey) group.dates.add(dateKey);
    groups.set(key, group);
  }

  return [...groups.values()]
    .map((group) => ({
      ...group,
      teeth: [...group.teeth].sort((a, b) => a - b),
      dates: [...group.dates].sort().reverse(),
    }))
    .sort(
      (a, b) =>
        b.sortValue - a.sortValue ||
        a.shortLabel.localeCompare(b.shortLabel, "fr"),
    );
}

export function getLastClinicalSession(
  actes: CategorizedLogosActeData[],
  now: DateTime = DateTime.now(),
) {
  if (!now.isValid) return null;
  const sessions = new Map<
    string,
    {
      date: string;
      actes: CategorizedLogosActeData[];
      notes: CategorizedLogosActeData[];
    }
  >();
  let latestDate: string | null = null;
  let latestTimestamp = -Infinity;
  for (const acte of actes) {
    if (acte.category_type !== "with_code" && acte.category_type !== "comment")
      continue;
    const date = getPatientDate(acte.timestamp);
    const dateKey = date.toISODate();
    if (!dateKey || date > now) continue;
    const session = sessions.get(dateKey) ?? {
      date: dateKey,
      actes: [],
      notes: [],
    };
    if (acte.category_type === "with_code") {
      session.actes.push(acte);
      if (date.toMillis() > latestTimestamp) {
        latestDate = dateKey;
        latestTimestamp = date.toMillis();
      }
    } else session.notes.push(acte);
    sessions.set(dateKey, session);
  }
  return latestDate ? (sessions.get(latestDate) ?? null) : null;
}

export function getLatestConsultation(consultations: Consultation[]) {
  return (
    consultations
      .filter(
        (consultation) =>
          getPatientDate(consultation.recordingStartedAt).isValid &&
          (consultation.validatedReport?.trim() ||
            consultation.draftReport?.trim()),
      )
      .sort(
        (a, b) =>
          getPatientDate(b.recordingStartedAt).toMillis() -
          getPatientDate(a.recordingStartedAt).toMillis(),
      )[0] ?? null
  );
}

export function groupOverviewQuotes(quotes: PatientOverviewQuote[]) {
  const accepted: PatientOverviewQuote[] = [];
  const proposed: PatientOverviewQuote[] = [];
  const archived: PatientOverviewQuote[] = [];
  for (const quote of [...quotes].sort(
    (a, b) => b.creation_date.getTime() - a.creation_date.getTime(),
  )) {
    if (
      quote.realization?.state === "performed" ||
      !["accepted", "given", "created"].includes(quote.tracking.state)
    ) {
      archived.push(quote);
    } else if (quote.tracking.state === "accepted") {
      accepted.push(quote);
    } else {
      proposed.push(quote);
    }
  }
  return { accepted, proposed, archived };
}

export function getQuoteCoverage(
  documents: PecDocumentListItem[],
  quoteId: string,
) {
  const linked = documents
    .filter((document) => document.quoteId === quoteId)
    .sort(
      (a, b) =>
        getPatientDate(b.capturedAt).toMillis() -
          getPatientDate(a.capturedAt).toMillis() || b.id.localeCompare(a.id),
    );
  const latest = linked[0] ?? null;
  // Never discard a newer request in favour of an older, approved response.
  const response = latest?.documentType === "coverage_response" ? latest : null;
  const review = response?.coverageReviews.at(-1) ?? null;
  return { documents: linked, latest, response, review };
}

export function getCoverageExpiry(
  document: PecDocumentListItem,
  now: DateTime = DateTime.now(),
) {
  if (!now.isValid) return null;
  const value = document.ocrData?.peremptionDate;
  const expiry = value
    ? DateTime.fromFormat(value, "dd/MM/yyyy", { zone: "Europe/Paris" })
    : null;
  if (!expiry?.isValid) return null;
  return { date: value, expired: expiry.endOf("day") < now };
}
